import React from "react";

const INLINE_PATTERN =
  /\[([^\]]+)\]\(([^)]+)\)|`([^`]+)`|\*\*([^*]+)\*\*|__([^_]+)__|\*([^*]+)\*|_([^_]+)_/g;

const isTableRow = (line) => /^\s*\|.*\|\s*$/.test(line);
const isTableSeparator = (line) => /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);

const splitTableRow = (line) =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());

const isBlockStart = (line) =>
  /^#{1,6}\s/.test(line) ||
  /^\s*>\s?/.test(line) ||
  /^\s*[-*+]\s+/.test(line) ||
  /^\s*\d+\.\s+/.test(line) ||
  /^```/.test(line) ||
  isTableRow(line);

const sanitizeHref = (href) => {
  if (typeof href !== "string") {
    return null;
  }

  const trimmedHref = href.trim();

  if (!trimmedHref) {
    return null;
  }

  if (trimmedHref.startsWith("/")) {
    return trimmedHref;
  }

  try {
    const url = new URL(trimmedHref);

    return ["http:", "https:", "mailto:"].includes(url.protocol)
      ? trimmedHref
      : null;
  } catch (error) {
    return null;
  }
};

const renderInline = (text, keyPrefix) => {
  if (!text) {
    return null;
  }

  const matches = [...text.matchAll(INLINE_PATTERN)];

  if (!matches.length) {
    return text;
  }

  const nodes = [];
  let lastIndex = 0;

  matches.forEach((match, index) => {
    const [fullMatch, linkLabel, linkHref, inlineCode, strongStars, strongUnderscore, emStars, emUnderscore] =
      match;
    const matchIndex = match.index || 0;
    const nodeKey = `${keyPrefix}-${index}`;

    if (matchIndex > lastIndex) {
      nodes.push(text.slice(lastIndex, matchIndex));
    }

    if (linkLabel && linkHref) {
      const safeHref = sanitizeHref(linkHref);

      if (!safeHref) {
        nodes.push(fullMatch);
      } else {
        nodes.push(
          <a
            key={nodeKey}
            href={safeHref}
            target="_blank"
            rel="noreferrer"
          >
            {renderInline(linkLabel, `${nodeKey}-label`)}
          </a>
        );
      }
    } else if (inlineCode) {
      nodes.push(<code key={nodeKey}>{inlineCode}</code>);
    } else if (strongStars || strongUnderscore) {
      nodes.push(
        <strong key={nodeKey}>
          {renderInline(strongStars || strongUnderscore, `${nodeKey}-strong`)}
        </strong>
      );
    } else if (emStars || emUnderscore) {
      nodes.push(
        <em key={nodeKey}>
          {renderInline(emStars || emUnderscore, `${nodeKey}-em`)}
        </em>
      );
    } else {
      nodes.push(fullMatch);
    }

    lastIndex = matchIndex + fullMatch.length;
  });

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
};

const renderBlocks = (markdown, keyPrefix = "md") => {
  const source = typeof markdown === "string" ? markdown : String(markdown || "");
  const lines = source.split(/\r?\n/);
  const blocks = [];
  let lineIndex = 0;
  let blockIndex = 0;

  while (lineIndex < lines.length) {
    const currentLine = lines[lineIndex];

    if (!currentLine.trim()) {
      lineIndex += 1;
      continue;
    }

    if (/^```/.test(currentLine)) {
      const codeLines = [];
      lineIndex += 1;

      while (lineIndex < lines.length && !/^```/.test(lines[lineIndex])) {
        codeLines.push(lines[lineIndex]);
        lineIndex += 1;
      }

      if (lineIndex < lines.length) {
        lineIndex += 1;
      }

      blocks.push(
        <pre key={`${keyPrefix}-block-${blockIndex}`}>
          <code>{codeLines.join("\n")}</code>
        </pre>
      );
      blockIndex += 1;
      continue;
    }

    const headingMatch = currentLine.match(/^(#{1,6})\s+(.*)$/);

    if (headingMatch) {
      const headingLevel = headingMatch[1].length;
      const HeadingTag = `h${headingLevel}`;

      blocks.push(
        <HeadingTag key={`${keyPrefix}-block-${blockIndex}`}>
          {renderInline(headingMatch[2].trim(), `${keyPrefix}-heading-${blockIndex}`)}
        </HeadingTag>
      );
      lineIndex += 1;
      blockIndex += 1;
      continue;
    }

    if (/^\s*>\s?/.test(currentLine)) {
      const quoteLines = [];

      while (lineIndex < lines.length && /^\s*>\s?/.test(lines[lineIndex])) {
        quoteLines.push(lines[lineIndex].replace(/^\s*>\s?/, ""));
        lineIndex += 1;
      }

      blocks.push(
        <blockquote key={`${keyPrefix}-block-${blockIndex}`}>
          {renderBlocks(quoteLines.join("\n"), `${keyPrefix}-quote-${blockIndex}`)}
        </blockquote>
      );
      blockIndex += 1;
      continue;
    }

    if (/^\s*[-*+]\s+/.test(currentLine)) {
      const items = [];
      const currentBlockIndex = blockIndex;

      while (lineIndex < lines.length && /^\s*[-*+]\s+/.test(lines[lineIndex])) {
        items.push(lines[lineIndex].replace(/^\s*[-*+]\s+/, "").trim());
        lineIndex += 1;
      }

      blocks.push(
        <ul key={`${keyPrefix}-block-${currentBlockIndex}`}>
          {items.map((item, index) => (
            <li key={`${keyPrefix}-ul-${currentBlockIndex}-${index}`}>
              {renderInline(
                item,
                `${keyPrefix}-ul-inline-${currentBlockIndex}-${index}`
              )}
            </li>
          ))}
        </ul>
      );
      blockIndex += 1;
      continue;
    }

    if (/^\s*\d+\.\s+/.test(currentLine)) {
      const items = [];
      const currentBlockIndex = blockIndex;

      while (lineIndex < lines.length && /^\s*\d+\.\s+/.test(lines[lineIndex])) {
        items.push(lines[lineIndex].replace(/^\s*\d+\.\s+/, "").trim());
        lineIndex += 1;
      }

      blocks.push(
        <ol key={`${keyPrefix}-block-${currentBlockIndex}`}>
          {items.map((item, index) => (
            <li key={`${keyPrefix}-ol-${currentBlockIndex}-${index}`}>
              {renderInline(
                item,
                `${keyPrefix}-ol-inline-${currentBlockIndex}-${index}`
              )}
            </li>
          ))}
        </ol>
      );
      blockIndex += 1;
      continue;
    }

    // GFM table: header row + separator row (+ body rows)
    if (
      isTableRow(currentLine) &&
      lineIndex + 1 < lines.length &&
      isTableSeparator(lines[lineIndex + 1])
    ) {
      const headerCells = splitTableRow(currentLine);
      const bodyRows = [];
      lineIndex += 2;

      while (lineIndex < lines.length && isTableRow(lines[lineIndex])) {
        bodyRows.push(splitTableRow(lines[lineIndex]));
        lineIndex += 1;
      }

      const currentBlockIndex = blockIndex;
      blocks.push(
        <table key={`${keyPrefix}-block-${currentBlockIndex}`}>
          <thead>
            <tr>
              {headerCells.map((cell, index) => (
                <th key={`${keyPrefix}-th-${currentBlockIndex}-${index}`}>
                  {renderInline(cell, `${keyPrefix}-th-inline-${currentBlockIndex}-${index}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bodyRows.map((row, rowIndex) => (
              <tr key={`${keyPrefix}-tr-${currentBlockIndex}-${rowIndex}`}>
                {row.map((cell, cellIndex) => (
                  <td key={`${keyPrefix}-td-${currentBlockIndex}-${rowIndex}-${cellIndex}`}>
                    {renderInline(
                      cell,
                      `${keyPrefix}-td-inline-${currentBlockIndex}-${rowIndex}-${cellIndex}`
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
      blockIndex += 1;
      continue;
    }

    const paragraphLines = [currentLine.trim()];
    lineIndex += 1;

    while (
      lineIndex < lines.length &&
      lines[lineIndex].trim() &&
      !isBlockStart(lines[lineIndex])
    ) {
      paragraphLines.push(lines[lineIndex].trim());
      lineIndex += 1;
    }

    blocks.push(
      <p key={`${keyPrefix}-block-${blockIndex}`}>
        {renderInline(
          paragraphLines.join(" "),
          `${keyPrefix}-paragraph-${blockIndex}`
        )}
      </p>
    );
    blockIndex += 1;
  }

  return blocks;
};

const MarkdownRenderer = ({ children }) => <>{renderBlocks(children)}</>;

export default MarkdownRenderer;
